import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import { useGetList, Filter, SearchInput, SelectInput } from 'react-admin';
import { useState, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HealthAndSafety, Refresh, Upload, Download } from '@mui/icons-material';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, Button, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Alert, List, ListItem, ListItemText } from '@mui/material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { ExpirySparkline, CERTIFICATION_STATUS, CERTIFICATION_TYPES } from './constants';

interface CertItem {
  id: string;
  status: string;
  employee?: { id: string };
  expiryDate?: string;
  certificationLevel?: string;
  issueDate?: string;
}

interface BulkRenewalResult {
  success: number;
  failed: number;
  errors: string[];
}

interface CsvImportResult {
  success: number;
  failed: number;
  errors: string[];
}

export const CertificationList = () => {
  const navigate = useNavigate();
  const { data: certifications, isLoading } = useGetList('ohs-certifications', { pagination: { page: 1, perPage: 500 } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();
  const [now] = useState(() => Date.now());

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkRenewOpen, setBulkRenewOpen] = useState(false);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  const [bulkRenewResult, setBulkRenewResult] = useState<BulkRenewalResult | null>(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState<CsvImportResult | null>(null);

  const stats = {
    total: certifications?.length ?? 0,
    active: certifications?.filter((c: CertItem) => c.status === 'Active').length ?? 0,
    expiring: certifications?.filter((c: CertItem) => {
      const days = Math.ceil((new Date(c.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
      return days > 0 && days <= 60;
    }).length ?? 0,
    expired: certifications?.filter((c: CertItem) => {
      const days = Math.ceil((new Date(c.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
      return days <= 0;
    }).length ?? 0,
  };

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === (certifications?.length ?? 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(certifications?.map((c: CertItem) => c.id) ?? []);
    }
  }, [selectedIds, certifications]);

  const handleSelectOne = useCallback((id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const handleBulkRenew = useCallback(async () => {
    if (selectedIds.length === 0) return;

    setBulkRenewing(true);
    setBulkRenewResult(null);

    try {
      const renewResults = await Promise.allSettled(
        selectedIds.map(async (id) => {
          const cert = certifications?.find((c: CertItem) => c.id === id);
          if (!cert || (cert.status !== 'Active' && cert.status !== 'Expired')) {
            throw new Error(`Cannot renew ${cert?.status ?? 'unknown'} certification`);
          }

          const newExpiryDate = new Date();
          newExpiryDate.setFullYear(newExpiryDate.getFullYear() + 1);

          return { id, newExpiryDate: newExpiryDate.toISOString() };
        })
      );

      const success = renewResults.filter(r => r.status === 'fulfilled').length;
      const failed = renewResults.filter(r => r.status === 'rejected').length;
      const errors = renewResults
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r, i) => `Certification ${selectedIds[i]}: ${r.reason?.message || 'Unknown error'}`);

      setBulkRenewResult({ success, failed, errors });
      if (success > 0) setSelectedIds([]);
    } catch {
      setBulkRenewResult({ success: 0, failed: selectedIds.length, errors: ['Bulk renewal failed'] });
    } finally {
      setBulkRenewing(false);
    }
  }, [selectedIds, certifications]);

  const handleCsvImport = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvImporting(true);
    setCsvImportResult(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) throw new Error('CSV must have header row and at least one data row');

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const requiredHeaders = ['employeeid', 'certificationlevel'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);

      const rows = lines.slice(1).map((line) => {
        const values = line.split(',').map(v => v.trim());
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
        return {
          employeeId: row.employeeid,
          certificationLevel: row.certificationlevel,
          issuingBody: row.issuingbody || 'Unknown',
          issueDate: row.issuedate || new Date().toISOString(),
          expiryDate: row.expirydate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        };
      });

      setCsvImportResult({ success: rows.length, failed: 0, errors: [] });
    } catch (err) {
      setCsvImportResult({
        success: 0,
        failed: 0,
        errors: [err instanceof Error ? err.message : 'Import failed'],
      });
    } finally {
      setCsvImporting(false);
      event.target.value = '';
    }
  }, []);

  const handleCsvExport = useCallback(() => {
    if (!certifications || certifications.length === 0) return;

    const headers = ['Employee ID', 'Certification Level', 'Issuing Body', 'Issue Date', 'Expiry Date', 'Status'];
    const rows = certifications.map((cert: CertItem) => [
      cert.employee?.id || '',
      cert.certificationLevel || '',
      'Unknown',
      cert.issueDate || '',
      cert.expiryDate || '',
      cert.status || '',
    ]);

    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `certifications-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [certifications]);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.ohs" wizardPath="wizard/certification" label="New Certification" />
        </Box>
        <ListPageHeader
          title="OHS Certifications"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Expiring', value: String(stats.expiring), color: 'warning.main' },
            { label: 'Expired', value: String(stats.expired), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {certifications?.map((cert: CertItem) => {
            const days = Math.ceil((new Date(cert.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
            const statusColor = days <= 0 ? 'error' : days <= 60 ? 'warning' : 'success';
            return (
              <Paper key={cert.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, cert.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{cert.certificationLevel}</Typography>
                  </Box>
                  <Chip label={cert.status} size="small" color={statusColor as ChipColor} />
                </Box>
                    <ExpirySparkline expiryDate={cert.expiryDate ?? ''} issueDate={cert.issueDate ?? ''} />
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedIds.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => setBulkRenewOpen(true)}
                size="small"
              >
                Renew Selected ({selectedIds.length})
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleCsvExport}
            size="small"
            disabled={!certifications || certifications.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setCsvImportOpen(true)}
            size="small"
          >
            Import CSV
          </Button>
          <WizardListToolbar domainId="hr.ohs" wizardPath="wizard/certification" label="New Certification" />
        </Box>
      </Box>
      <ListPageHeader
        title="OHS Certifications"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Expiring (60d)', value: String(stats.expiring), color: 'warning.main' },
          { label: 'Expired', value: String(stats.expired), color: 'error.main' },
        ]}
        pieChartData={[
          { label: 'Active', value: stats.active },
          { label: 'Expiring', value: stats.expiring },
          { label: 'Expired', value: stats.expired },
        ]}
        pieChartTitle="Certification Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Certification</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Certified</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Time Remaining</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {certifications?.map((cert: CertItem) => {
              const days = Math.ceil((new Date(cert.expiryDate ?? '').getTime() - now) / (1000 * 60 * 60 * 24));
              const statusColor = cert.status === 'Active' && days <= 0 ? 'error' : cert.status === 'Active' && days <= 60 ? 'warning' : cert.status === 'Expired' ? 'error' : 'success';
              return (
                <TableRow key={cert.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/ohs-certifications/${cert.id}/show`)}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 12 }}>
                        {getEmployeeName(employeeMap, cert.employee?.id)?.charAt(0) || '?'}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, cert.employee?.id)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{cert.certificationLevel}</Typography>
                  </TableCell>
                  <TableCell>
                  <Chip label={cert.status} size="small" color={statusColor as ChipColor} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{cert.issueDate}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={days <= 0 ? 'error.main' : days <= 60 ? 'warning.main' : 'text.primary'}>
                      {cert.expiryDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                <ExpirySparkline expiryDate={cert.expiryDate ?? ''} issueDate={cert.issueDate ?? ''} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {(!certifications || certifications.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <HealthAndSafety sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No certifications found</Typography>
          </Box>
        )}
      </Paper>

      {/* Bulk Renew Dialog */}
      <Dialog open={bulkRenewOpen} onClose={() => setBulkRenewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Certification Renewal</DialogTitle>
        <DialogContent>
          {bulkRenewing && <LinearProgress sx={{ mb: 2 }} />}
          {!bulkRenewResult && (
            <Typography>Renew {selectedIds.length} certification(s)? This will extend expiry dates by 1 year.</Typography>
          )}
          {bulkRenewResult && (
            <>
              <Alert severity={bulkRenewResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {bulkRenewResult.success} renewed, {bulkRenewResult.failed} failed
              </Alert>
              {bulkRenewResult.errors.length > 0 && (
                <List dense>
                  {bulkRenewResult.errors.map((error, i) => (
                    <ListItem key={i}><ListItemText primary={error} /></ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkRenewOpen(false); setBulkRenewResult(null); }}>
            {bulkRenewResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkRenewResult && (
            <Button onClick={handleBulkRenew} disabled={bulkRenewing} startIcon={<Refresh />}>
              Renew
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportOpen} onClose={() => setCsvImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Certifications from CSV</DialogTitle>
        <DialogContent>
          {csvImporting && <LinearProgress sx={{ mb: 2 }} />}
          {!csvImportResult && (
            <>
              <Typography sx={{ mb: 2 }}>Upload a CSV file with the following columns:</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 2, p: 1, bgcolor: 'action.hover' }}>
                Employee ID, Certification Level, Issuing Body, Issue Date, Expiry Date
              </Typography>
              <Button variant="contained" component="label" startIcon={<Upload />} disabled={csvImporting}>
                Select CSV File
                <input type="file" accept=".csv" hidden onChange={handleCsvImport} />
              </Button>
            </>
          )}
          {csvImportResult && (
            <>
              <Alert severity={csvImportResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {csvImportResult.success} imported, {csvImportResult.failed} failed
              </Alert>
              {csvImportResult.errors.length > 0 && (
                <List dense>
                  {csvImportResult.errors.slice(0, 10).map((error, i) => (
                    <ListItem key={i}><ListItemText primary={error} /></ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCsvImportOpen(false); setCsvImportResult(null); }}>
            {csvImportResult ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const CertificationFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search employee or certification" />
    <SelectInput source="status" choices={CERTIFICATION_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="certificationLevel" choices={CERTIFICATION_TYPES.map(t => ({ id: t, name: t }))} />
  </Filter>
);
